import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Lock, Truck } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import authService from '@/services/authService';

const ResetPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { theme } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast({
        title: "Invalid Request",
        description: "Password reset token is missing",
        variant: "destructive",
      });
      return;
    }
    
    if (!password || !confirmPassword) {
      toast({
        title: "Password Required",
        description: "Please enter and confirm your new password",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    try {
      const result = await authService.resetPassword(token, password);

      if (!result.success) {
        throw new Error(result.error || 'Failed to reset password');
      }
      
      setResetSuccess(true);
      toast({
        title: "Password Reset Successful",
        description: "Your password has been updated successfully",
      });
      
      
      setTimeout(() => {
        navigate('/login');
      }, 100);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="md:w-1/2 hidden md:flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-primary/5 to-background">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative z-10 text-center p-8 max-w-md">
          <div className="flex justify-center mb-8">
            <img 
              src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" 
              alt="IPL Fleet Tracking Logo" 
              className="h-40 object-contain mx-auto"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-foreground mb-4">
            IPL VLTS
          </h1>
          
          <p className="text-lg text-foreground/80 mb-2">
           VLTS management solution
          </p>
          
          <p className="text-base text-foreground/60">
            Track, manage, and optimize your VLTS operations with real-time insights and analytics.
          </p>
        </div>
      </div>
      
      <div className="md:w-1/2 w-full flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <Card className={`border-0 bg-transparent shadow-none ${theme}`}>
            <CardHeader className="text-center space-y-4 px-0 pb-6">
              <div className="flex justify-center md:hidden mb-4">
                <img 
                  src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" 
                  alt="IPL VLTS Tracking Logo" 
                  className="h-16 object-contain"
                />
              </div>
              <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <Truck className="w-8 h-8 text-primary-foreground" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-3xl font-bold text-foreground">
                  {resetSuccess ? "Password Updated" : "Reset Password"}
                </CardTitle>
                <CardDescription className="text-base">
                  {resetSuccess 
                    ? "Your password has been successfully reset" 
                    : "Enter your new password below"}
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent className="px-0">
              {resetSuccess ? (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-foreground mb-4">
                    Your password has been successfully updated
                  </p>
                  <div className="mt-6">
                    <CommonButton 
                      onClick={() => navigate('/login')}
                      className="w-full h-12 text-base font-semibold rounded-lg"
                    >
                      Go to Login
                    </CommonButton>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-foreground text-sm font-medium">
                      New Password
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12 text-base border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Password must be at least 6 characters
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-foreground text-sm font-medium">
                      Confirm New Password
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 pr-10 h-12 text-base border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <CommonButton 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold rounded-lg"
                    loading={loading}
                  >
                    Update Password
                  </CommonButton>
                </form>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 px-0 pt-6">
              <div className="text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary hover:underline"
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
              <div className="text-center text-xs text-muted-foreground/60">
                <p>© 2025 IPL VLTS Platform</p>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;